import { useState } from 'react';
import { useAuthContext } from './AuthContext';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';
import './App.css';
import { plans } from './plans';

export default function PricingPage() {
  const { user, profile } = useAuthContext();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  // Normalize unknown/legacy tier strings to free so the "Current Plan"
  // badge resolves correctly. Matches AccountPage.js.
  const KNOWN = new Set(['free', 'creator', 'pro', 'admin']);
  const rawTier = profile?.subscriptionTier || 'free';
  const currentTier = KNOWN.has(rawTier) ? rawTier : 'free';

  const handleSubscribe = async (planId) => {
    if (!user) { setError('Sign in first'); return; }
    setLoading(planId);
    setError('');
    try {
      const functions = getFunctions();
      const createCheckout = httpsCallable(functions, 'createSubscriptionCheckout');
      const { data } = await createCheckout({ planId });
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  const handleManage = async () => {
    setLoading('manage');
    try {
      const functions = getFunctions();
      const manage = httpsCallable(functions, 'manageSubscription');
      const { data } = await manage();
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '40px auto', padding: '0 24px' }}>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <h1 style={{ fontSize: '1.8rem', color: '#f8fafc', marginBottom: 8 }}>Plans & Pricing</h1>
        <p style={{ color: '#94a3b8', fontSize: '0.95rem', maxWidth: 500, margin: '0 auto' }}>
          Everything local is free forever. Pay only for cloud features that cost us real money.
        </p>
      </div>

      {error && (
        <div style={{ textAlign: 'center', color: '#f87171', marginBottom: 20, fontSize: '0.85rem' }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {plans.map((plan) => {
          const isCurrent = plan.id === currentTier;
          return (
            <div key={plan.id} style={{
              background: plan.highlight ? '#1e3a5f' : '#1e293b',
              borderRadius: 14, padding: 24,
              border: plan.highlight ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 500, marginBottom: 4 }}>
                {plan.name}
              </div>
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: '1.8rem', fontWeight: 700, color: '#f8fafc' }}>{plan.price}</span>
                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{plan.period}</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '4px 0', display: 'flex', gap: 8 }}>
                    <span style={{ color: '#4ade80' }}>+</span> {f}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <div style={{ textAlign: 'center' }}>
                  <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: 500 }}>Current Plan</span>
                  {currentTier !== 'free' && (
                    <button onClick={handleManage} disabled={loading === 'manage'}
                      style={{ ...btnStyle, background: 'transparent', color: '#94a3b8', border: '1px solid #334155', marginTop: 8, width: '100%' }}>
                      {loading === 'manage' ? 'Loading...' : 'Manage'}
                    </button>
                  )}
                </div>
              ) : (
                <button onClick={() => handleSubscribe(plan.id)} disabled={!!loading || plan.disabled}
                  style={{ ...btnStyle, background: plan.highlight ? '#3b82f6' : '#f8fafc', color: plan.highlight ? '#fff' : '#0f172a', width: '100%' }}>
                  {loading === plan.id ? 'Redirecting...' : plan.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', marginTop: 32, color: '#64748b', fontSize: '0.8rem' }}>
        Everything local is free forever. Pay only for the cloud features that cost us real money —
        storage, bandwidth, publishing privately, and holding your iPhone's check-ins until your Mac is back.
      </div>
    </div>
  );
}

const btnStyle = {
  padding: '10px 20px', borderRadius: 20, border: 'none',
  fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
